/**
 * Purchase mode — Buy vs Rent.
 *
 * Why this exists:
 *   Budget means two entirely different things to a buyer ("A$900k–1.5M") vs
 *   a renter ("$550–750/wk"). Without this axis, the budget pill cannot show
 *   a real, believable number — it falls back to vague words like "Moderate".
 *   Users told us plainly: "budget must be instantly understandable".
 *
 * Design rules:
 *   - One global state, subscriber pattern mirroring `constraint-priority` and
 *     `budget-band` so the next dev reads the pattern once.
 *   - No new scoring — this axis only rewords display labels (via
 *     `budget-format`) and (later) adjusts the band price ranges. Internal
 *     `BudgetBand` enum is unchanged so shortlist/compare/classifyFit keep
 *     compiling.
 *   - Persisted in localStorage so a user who has set themselves up as a
 *     "renter on $650/wk" doesn't have to restate it every session.
 */

export type PurchaseMode = "buy" | "rent";

const STORAGE_KEY = "hyperlocal-purchase-mode";

let current: PurchaseMode = loadInitial();
const listeners = new Set<(m: PurchaseMode) => void>();

function loadInitial(): PurchaseMode {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "buy" || v === "rent") return v;
    }
  } catch {
    /* storage blocked — fall through */
  }
  return "buy";
}

export function getPurchaseMode(): PurchaseMode {
  return current;
}

export function setPurchaseMode(m: PurchaseMode): void {
  if (m === current) return;
  current = m;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, m);
    }
  } catch {
    /* ignore */
  }
  for (const fn of Array.from(listeners)) {
    try {
      fn(m);
    } catch {
      /* a broken subscriber must not break the rest */
    }
  }
}

export function subscribePurchaseMode(
  fn: (m: PurchaseMode) => void
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getPurchaseModeLabel(m: PurchaseMode = current): string {
  return m === "rent" ? "Rent" : "Buy";
}
